import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Shield, MapPin, Users, Truck, Package, Phone, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';

const Home = () => {
  const features = [
    {
      icon: Clock,
      title: 'Lightning Fast',
      description: 'Same-day and next-day delivery options to meet your urgent needs',
    },
    {
      icon: Shield,
      title: 'Reliable & Secure',
      description: 'Fully insured deliveries with real-time tracking and proof of delivery',
    },
    {
      icon: MapPin,
      title: 'Wide Coverage',
      description: 'Nationwide UK delivery and international shipping to Europe',
    },
    {
      icon: Users,
      title: 'Dedicated Support',
      description: '24/7 customer service team ready to assist with all your queries',
    },
  ];

  const services = [
    {
      icon: Truck,
      title: 'Same-Day Delivery',
      description: 'Urgent packages delivered within hours across major UK cities',
      image: 'https://images.unsplash.com/photo-1665521032636-e8d2f6927053?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxkZWxpdmVyeSUyMHZhbnxlbnwwfHx8fDE3NjQ3OTIwMTd8MA&ixlib=rb-4.1.0&q=85',
    },
    {
      icon: Package,
      title: 'Next-Day Delivery',
      description: 'Reliable overnight delivery service to any UK destination',
      image: 'https://images.pexels.com/photos/4391470/pexels-photo-4391470.jpeg',
    },
    {
      icon: MapPin,
      title: 'Multi-Drop Services',
      description: 'Efficient multiple delivery stops on a single route, perfect for businesses',
      image: '/assets/delivery-service.jpg',
    },
    {
      icon: Package,
      title: 'Storage Solutions',
      description: 'Secure warehouse storage for short-term and long-term needs',
      image: '/assets/warehouse.jpeg',
    },
  ];

  const steps = [
    { number: '01', title: 'Book Your Delivery', description: 'Request a quote online or call our team' },
    { number: '02', title: 'We Collect', description: 'Our driver picks up from your location' },
    { number: '03', title: 'Safe Transit', description: 'Your items are transported securely' },
    { number: '04', title: 'Delivered & Tracked', description: 'Receive confirmation and tracking updates' },
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      company: 'TechStart Solutions',
      text: 'Idris Express has been our go-to courier for over a year. Their same-day service is incredibly reliable and the tracking system keeps us informed every step of the way.',
    },
    {
      name: 'Michael Chen',
      company: 'Global Imports Ltd',
      text: 'Outstanding service! They handle our logistics and storage needs with care and professionalism. The team is responsive and always goes the extra mile.',
    },
    {
      name: 'Emma Williams',
      company: 'E-Commerce Pro',
      text: "As an online retailer, we depend on fast delivery. Idris Express never disappoints. Their next-day service has helped us maintain excellent customer satisfaction.",
    },
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Fast Courier & Delivery Services UK | Same-Day Delivery | Idris Express"
        description="Professional courier services across the UK & Europe. Same-day, next-day delivery, multi-drop services & storage solutions. Call 07481 435244 for a quote."
        keywords="courier service, delivery uk, same day delivery, next day delivery, parcel delivery, courier near me, express delivery, logistics, freight, storage solutions, multi-drop delivery, nationwide courier, europe delivery"
        canonicalUrl="https://idrisexpressltd.co.uk/"
      />
      {/* Hero Section */}
      <section
        className="relative h-[600px] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/assets/hero-bg.jpg)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Fast, Reliable Delivery
            <br />
            <span className="text-gray-300">Across the UK and Europe</span>
          </h1>
          <p className="text-xl text-gray-200 mb-8 max-w-3xl mx-auto">
            Expert courier services offering same-day, next-day, and scheduled deliveries. From single parcels to freight solutions, we deliver with speed and care.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:07481435244" aria-label="Call Idris Express Ltd">
              <Button size="lg" className="bg-white text-gray-900 hover:bg-gray-100 text-lg px-8 py-6">
                <Phone className="mr-2 h-5 w-5" />
                Call Now
              </Button>
            </a>
            <a href="https://wa.me/447481435244" target="_blank" rel="noopener noreferrer" aria-label="Chat with Idris Express on WhatsApp">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 text-lg px-8 py-6"
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Idris Express Ltd?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We combine cutting-edge technology with personal service to deliver excellence every time
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div className="p-4 bg-gray-100 rounded-full">
                      <feature.icon className="h-8 w-8 text-gray-800" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive delivery solutions tailored to your business needs
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Card
                key={index}
                className="group overflow-hidden border-none shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <service.icon className="h-8 w-8 text-white" />
                  </div>
                </div>
                <CardContent className="pt-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <Link
                    to="/services"
                    className="text-gray-800 font-medium hover:text-gray-600 inline-flex items-center"
                    aria-label={`Learn more about ${service.title} service`}
                  >
                    Learn More About {service.title}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">How It Works</h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">Simple, efficient process from booking to delivery</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="text-6xl font-bold text-gray-700">{step.number}</div>
                  <h3 className="text-xl font-semibold">{step.title}</h3>
                  <p className="text-gray-400">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-12 left-full w-full h-0.5 bg-gray-700" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Trusted by businesses across the UK</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-none shadow-lg">
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <p className="text-gray-600 italic">"{testimonial.text}"</p>
                    <div className="border-t pt-4">
                      <p className="font-semibold text-gray-900">{testimonial.name}</p>
                      <p className="text-sm text-gray-500">{testimonial.company}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Request a quote today and experience the Idris Express difference
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:07481435244" aria-label="Call Idris Express Ltd">
              <Button size="lg" className="bg-white text-gray-900 hover:bg-gray-100 text-lg px-8 py-6">
                <Phone className="mr-2 h-5 w-5" />
                Call Now: 07481 435244
              </Button>
            </a>
            <a href="https://wa.me/447481435244" target="_blank" rel="noopener noreferrer" aria-label="Chat with Idris Express on WhatsApp">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 text-lg px-8 py-6"
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;